import {
  DesignObject,
  HumanAssistedResolutionObjectConfig,
  IconClass,
  ModuleReferenceObjectConfig,
  NodeTypeName,
  OasysObjectConfig,
  ObjectClass,
  TrustedResponseObjectConfig,
  ValueOrVariableType
} from '@app/types';

type ExcludedObjectClass = (
  ObjectClass.banner
  | ObjectClass.base
  | ObjectClass.begin
  | ObjectClass.custom
  | ObjectClass.data
  | ObjectClass.dataAccessCompletion
  | ObjectClass.dtmf
  | ObjectClass.exit
  | ObjectClass.flushPrompts
  | ObjectClass.input
  | ObjectClass.loopEscalation
  | ObjectClass.note
  | ObjectClass.playSavedPrompt
  | ObjectClass.prompt
  | ObjectClass.promptReference
  | ObjectClass.report
  | ObjectClass.richText
  | ObjectClass.savePrompt
  | ObjectClass.session
  | ObjectClass.sessionCompletion
  | ObjectClass.tab
  | ObjectClass.taskOrchestration
  | ObjectClass.vxmlRender
  | ObjectClass.withFiller
);

type ClassToConfig = {
  [ObjectClass.humanAssistedResolution]: HumanAssistedResolutionObjectConfig;
  [ObjectClass.moduleReference]: ModuleReferenceObjectConfig;
  [ObjectClass.oasys]: OasysObjectConfig;
  [ObjectClass.trustedResponse]: TrustedResponseObjectConfig;
};

type ClassToObject = {
  [K in keyof(ClassToConfig)]: {
    _class: K;
    props: Pick<Required<DesignObject<ClassToConfig[K]>['props']>, (
      'config'
      | 'iconClass'
      | 'nodeTypeName'
    )>
  }
};

/**
 * Defaults for newly created objects of each type.
 *
 * @note Seed.js is still the source of truth that this file is intended to slowly get rid of as we create *ObjectConfig types.
 */
export const defaultObjects = {
  [ObjectClass.humanAssistedResolution]: {
    _class: ObjectClass.humanAssistedResolution,
    props: {
      config: {
        description: 'Human Assisted Resolution Node',
        disabledInFlow: false,
        noActivityTimeout: '',
        note: ''
      },
      iconClass: IconClass.share2,
      nodeTypeName: NodeTypeName.humanAssistedResolution
    }
  },
  [ObjectClass.moduleReference]: {
    _class: ObjectClass.moduleReference,
    props: {
      config: {
        description: 'Run a module referenced by a variable.',
        inputs: [],
        moduleReference: '',
        outputs: []
      },
      iconClass: IconClass.command,
      nodeTypeName: NodeTypeName.moduleReference
    }
  },
  [ObjectClass.oasys]: {
    _class: ObjectClass.oasys,
    props: {
      config: {
        autoStartUp: { type: ValueOrVariableType.value, value: false },
        conversationVariables: { type: ValueOrVariableType.variable, varName: '' },
        description: 'Make calls to OASYS',
        disabledInFlow: false,
        domain: { type: ValueOrVariableType.value, value: '' },
        firstName: { type: ValueOrVariableType.variable, varName: '' },
        lastName: { type: ValueOrVariableType.variable, varName: '' },
        note: '',
        sensitiveData: false,
        transcript: { type: ValueOrVariableType.variable, varName: '' },
        transientVariables: { type: ValueOrVariableType.variable, varName: '' }
      },
      iconClass: IconClass.bot,
      nodeTypeName: NodeTypeName.oasys
    }
  },
  [ObjectClass.trustedResponse]: {
    _class: ObjectClass.trustedResponse,
    props: {
      config: {
        conversationHistory: {
          type: ValueOrVariableType.variable,
          varName: ''
        },
        curatedDataLimit: {
          type: ValueOrVariableType.value,
          value: 0
        },
        description: 'Query an LLM (optionally using a RAG).',
        response: {
          type: ValueOrVariableType.variable,
          varName: ''
        },
        sessionContext: {
          type: ValueOrVariableType.variable,
          varName: ''
        },
        task: {
          type: ValueOrVariableType.value,
          value: ''
        },
        userInput: {
          type: ValueOrVariableType.value,
          value: ''
        },
        useTrustedResponse: {
          type: ValueOrVariableType.value,
          value: true
        }
      },
      iconClass: IconClass.book,
      nodeTypeName: NodeTypeName.trustedResponse
    }
  }
} satisfies ClassToObject satisfies (
  // Throw an error if a new ObjectClass is added without adding it to @see ClassToConfig.
  // This satisfies must be separate from the first to not allow properties not yet in @see ClassToObject.
  Record<Exclude<ObjectClass, ExcludedObjectClass>, unknown>
);
